import { apiSlice } from "../../app/api/apiSlice";

const ADMIN_URL = '/users/';

export const adminApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getBuyers: builder.query({
      query: () => `${ADMIN_URL}buyers/`,
      providesTags: (result = [], error, arg) => [
        'Buyer',
        ...result.map(({ id }) => ({ type: 'Buyer', id })),
      ],
    }),
    createBuyer: builder.mutation({
      query: (buyerData) => ({
        url: `${ADMIN_URL}members/create_buyer/`,
        method: 'POST',
        body: buyerData,
      }),
      invalidatesTags: ['Buyer', 'Enterprise'],
    }),
    createClient: builder.mutation({
      query: (clientData) => ({
        url: `${ADMIN_URL}members/create_client/`,
        method: 'POST',
        body: clientData,
      }),
      invalidatesTags: ['Buyer', 'Enterprise'],
    }),
    getEnterprises: builder.query({
      query: () => `${ADMIN_URL}enterprises/`,
      providesTags: (result = [], error, arg) => [
        'Enterprise',
        ...result.map(({ id }) => ({ type: 'Enterprise', id })),
      ],
    }),
    createEnterprise: builder.mutation({
      query: (enterpriseData) => ({
        url: `${ADMIN_URL}enterprises/`,
        method: 'POST',
        body: enterpriseData,
      }),
      invalidatesTags: ['Enterprise'],
    }),
    updateEnterprise: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `${ADMIN_URL}enterprises/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Enterprise', id }, 'Enterprise'],
    }),
    deleteEnterprise: builder.mutation({
      query: (id) => ({
        url: `${ADMIN_URL}enterprises/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Enterprise', 'Buyer'],
    }),
    creditTokens: builder.mutation({
      query: ({ id, tokens }) => ({
        url: `${ADMIN_URL}enterprises/${id}/credit_tokens/`,
        method: 'POST',
        body: { tokens },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Enterprise', id: arg.id },
        'Enterprise',
      ],
    }),
    updateBuyer: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `${ADMIN_URL}members/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Buyer', 'Enterprise'],
    }),
    // Game Run History
    getEnterprisesWithTerminatedRuns: builder.query({
      query: () => '/users/admin-data/enterprises-with-terminated-runs/',
      providesTags: ['TerminatedGameRuns'],
    }),

    getTerminatedRunsByEnterprise: builder.query({
      query: (enterpriseId) => `/game/runs/terminated-by-enterprise/?enterprise_id=${enterpriseId}`,
      providesTags: (result, error, arg) => [{ type: 'TerminatedGameRuns', id: arg }],
    }),

    // All Game Runs (any status)
    getEnterprisesWithGameRuns: builder.query({
      query: () => '/users/admin-data/enterprises-with-game-runs/',
      providesTags: ['GameRun', 'Enterprise'],
    }),

    getGameRunsByEnterprise: builder.query({
      query: (enterpriseId) => `/game/runs/by-enterprise/?enterprise_id=${enterpriseId}`,
      providesTags: (result, error, arg) => [
        { type: 'GameRun', id: arg },
        ...(result || []).map(({ id }) => ({ type: 'GameRun', id })),
      ],
    }),

    // Set game run status (for admin)
    adminSetGameRunStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/game/runs/${id}/set-status/`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'GameRun', id: arg.id },
        'GameRun',
        'TerminatedGameRuns',
      ],
    }),

    // Delete game run (for admin)
    adminDeleteGameRun: builder.mutation({
      query: (id) => ({
        url: `/game/runs/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['GameRun', 'TerminatedGameRuns'],
    }),

    
    getGameExamples: builder.query({
      query: () => '/game/examples/',
      providesTags: (result = []) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'GameExample', id })),
              { type: 'GameExample', id: 'LIST' },
            ]
          : [{ type: 'GameExample', id: 'LIST' }],
    }),
    
    
    updateGameExample: builder.mutation({
      query: (example) => ({
        url: `/game/examples/${example.id}/`,
        method: 'PUT', // or 'PATCH' if partial
        body: example,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'GameExample', id: arg.id },
        { type: 'GameExample', id: 'LIST' },
      ],
    }),
    
    createGameExample: builder.mutation({
      query: (example) => ({
        url: '/game/examples/',
        method: 'POST',
        body: example,
      }),
      invalidatesTags: [{ type: 'GameExample', id: 'LIST' }],
    }),
    
    deleteGameExample: builder.mutation({
      query: (id) => ({
        url: `/game/examples/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'GameExample', id },
        { type: 'GameExample', id: 'LIST' },
      ],
    }),
    

    getVersions: builder.query({
        query: () => '/game/versions/',
        providesTags: ['Version']
    }),
    getGamePoints: builder.query({
        query: () => '/game/points/',
        providesTags: ['GamePoint']
    }),
    getScales: builder.query({
        query: () => '/game/scales/',
        providesTags: ['Scale']
    }),

    // Coaches endpoints
    getCoaches: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active);
        if (params?.availability) queryParams.append('availability', params.availability);
        if (params?.search) queryParams.append('search', params.search);
        const queryString = queryParams.toString();
        return `/game/coaches/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: (result = []) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Coach', id })),
              { type: 'Coach', id: 'LIST' },
            ]
          : [{ type: 'Coach', id: 'LIST' }],
    }),

    getCoach: builder.query({
      query: (id) => `/game/coaches/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Coach', id }],
    }),

    getAvailableCoaches: builder.query({
      query: () => '/game/coaches/available/',
      providesTags: [{ type: 'Coach', id: 'AVAILABLE' }],
    }),

    getCoachWorkload: builder.query({
      query: (id) => `/game/coaches/${id}/workload/`,
      providesTags: (result, error, id) => [{ type: 'CoachWorkload', id }],
    }),

    createCoach: builder.mutation({
      query: (coachData) => ({
        url: '/game/coaches/',
        method: 'POST',
        body: coachData,
      }),
      invalidatesTags: [{ type: 'Coach', id: 'LIST' }, { type: 'Coach', id: 'AVAILABLE' }],
    }),

    updateCoach: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/game/coaches/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Coach', id },
        { type: 'Coach', id: 'LIST' },
        { type: 'Coach', id: 'AVAILABLE' },
      ],
    }),

    deleteCoach: builder.mutation({
      query: (id) => ({
        url: `/game/coaches/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Coach', id: 'LIST' }, { type: 'Coach', id: 'AVAILABLE' }],
    }),

    resetCoachPassword: builder.mutation({
      query: (id) => ({
        url: `/game/coaches/${id}/reset_password/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Coach', id }],
    }),

    // Players endpoints
    getPlayers: builder.query({
      query: () => `${ADMIN_URL}members/?role=PLAYER`,
      providesTags: (result = []) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Player', id })),
              { type: 'Player', id: 'LIST' },
            ]
          : [{ type: 'Player', id: 'LIST' }],
    }),

    // Animators endpoints
    getAnimators: builder.query({
      query: () => `${ADMIN_URL}members/?role=ANIMATOR`,
      providesTags: (result = []) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Animator', id })),
              { type: 'Animator', id: 'LIST' },
            ]
          : [{ type: 'Animator', id: 'LIST' }],
    }),

    updateAnimator: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `${ADMIN_URL}members/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Animator', id },
        { type: 'Animator', id: 'LIST' },
      ],
    }),

    deleteAnimator: builder.mutation({
      query: (id) => ({
        url: `${ADMIN_URL}members/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Animator', id: 'LIST' }],
    }),

    resetAnimatorPassword: builder.mutation({
      query: (id) => ({
        url: `${ADMIN_URL}members/${id}/reset_password/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Animator', id }],
    }),

    // Coach members & animators (enterprise-scoped)
    getCoachEnterpriseMembers: builder.query({
      query: (enterpriseId) => `/gestion/members/?enterprise_id=${enterpriseId}`,
      providesTags: (result = [], error, arg) => [
        { type: 'CoachMember', id: arg },
        ...(result || []).map(({ id }) => ({ type: 'CoachMember', id })),
      ],
    }),
    createCoachMember: builder.mutation({
      query: (data) => ({
        url: '/gestion/members/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'CoachMember', id: arg.enterprise_id }],
    }),
    updateCoachMember: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/gestion/members/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'CoachMember', id: arg.id }],
    }),
    deleteCoachMember: builder.mutation({
      query: (id) => ({
        url: `/gestion/members/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CoachMember'],
    }),
    getCoachEnterpriseAnimators: builder.query({
      query: (enterpriseId) => `/gestion/animators/?enterprise_id=${enterpriseId}`,
      providesTags: (result = [], error, arg) => [
        'Animator',
        ...(result || []).map(({ id }) => ({ type: 'Animator', id })),
      ],
    }),

    // Coach game run management
    createCoachGameRun: builder.mutation({
      query: (data) => ({
        url: '/gestion/gameruns/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['GameRun'],
    }),
    updateCoachGameRun: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/gestion/gameruns/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'GameRun', id: arg.id }, 'GameRun'],
    }),
    getCoachGameGroups: builder.query({
      query: (gameRunId) => `/gestion/gamegroups/?gameRun=${gameRunId}`,
      providesTags: (result = [], error, arg) => [
        'GameGroup',
        ...(result || []).map(({ id }) => ({ type: 'GameGroup', id })),
      ],
    }),
    createCoachGroupsManually: builder.mutation({
      query: (groupData) => ({
        url: `/gestion/gameruns/${groupData.gameRunId}/create-groups-manually/`,
        method: 'POST',
        body: groupData,
      }),
      invalidatesTags: ['GameRun', 'GameGroup'],
    }),
  })
});

export const {
  useGetPlayersQuery,
  useGetBuyersQuery,
  useCreateBuyerMutation,
  useCreateClientMutation,
  useGetEnterprisesQuery,
  useCreateEnterpriseMutation,
  useUpdateEnterpriseMutation,
  useDeleteEnterpriseMutation,
  useCreditTokensMutation,
  useUpdateBuyerMutation,
  useGetEnterprisesWithTerminatedRunsQuery,
  useGetTerminatedRunsByEnterpriseQuery,
  useGetEnterprisesWithGameRunsQuery,
  useGetGameRunsByEnterpriseQuery,
  useAdminSetGameRunStatusMutation,
  useAdminDeleteGameRunMutation,
  useGetGameExamplesQuery,
  useCreateGameExampleMutation,
  useUpdateGameExampleMutation,
  useDeleteGameExampleMutation,
  useGetVersionsQuery,
  useGetGamePointsQuery,
  useGetScalesQuery,
  // Coaches
  useGetCoachesQuery,
  useGetCoachQuery,
  useGetAvailableCoachesQuery,
  useGetCoachWorkloadQuery,
  useCreateCoachMutation,
  useUpdateCoachMutation,
  useDeleteCoachMutation,
  useResetCoachPasswordMutation,
  // Animators
  useGetAnimatorsQuery,
  useUpdateAnimatorMutation,
  useDeleteAnimatorMutation,
  useResetAnimatorPasswordMutation,
  // Coach members & animators
  useGetCoachEnterpriseMembersQuery,
  useCreateCoachMemberMutation,
  useUpdateCoachMemberMutation,
  useDeleteCoachMemberMutation,
  useGetCoachEnterpriseAnimatorsQuery,
  // Coach game runs
  useCreateCoachGameRunMutation,
  useUpdateCoachGameRunMutation,
  useGetCoachGameGroupsQuery,
  useCreateCoachGroupsManuallyMutation,
} = adminApiSlice;
